import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import DashboardHeader from '../../components/DashboardHeader';
import ErrorMessage from '../../components/ErrorMessage';
import ErrorToast from '../../components/ErrorToast';
import FlagIcon from '../../components/FlagIcon';
import * as recipientsActions from '../../store/actions/recipients';
import * as transactionsActions from '../../store/actions/transactions';
import {
  canSendToRecipient,
  formatAccountDisplay,
  formatEuroAmount,
  formatEuroTotal,
  formatTransferDate,
  getBankLabel,
  getCountryInfo,
  getInitials,
  getRecipientEmail,
  getTransactionStatusColor,
  getTransactionStatusLabel,
  getTransactionsForRecipient,
  getTransactionTitle,
  isBusinessRecipient,
  isRecipientActive,
} from '../../utils/recipientHelpers';
import { useNumberFormat } from '../../hooks/useNumberFormat';

const CARD_CLASS = 'rounded-2xl border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-sm)]';
const TX_ROW_GRID = 'grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4';
const PENDING_STATUSES = ['Pending', 'Executing', 'Processing'];
const TX_ITEMS_PER_PAGE = 5;

function RecipientFlagBadge({ currencyCode }) {
  if (!currencyCode) return null;
  return (
    <span
      className='flex h-5 w-5 shrink-0 items-center justify-center rounded-[8px] border border-[var(--color-border)] bg-[var(--off)]'
      aria-hidden='true'
    >
      <FlagIcon currencyCode={currencyCode} className='h-2.5 w-3 rounded-sm' />
    </span>
  );
}

RecipientFlagBadge.propTypes = {
  currencyCode: PropTypes.string,
};

RecipientFlagBadge.defaultProps = {
  currencyCode: '',
};

function DeleteConfirmationModal({ isOpen, onClose, onConfirm, recipientName, isDeleting }) {
  if (!isOpen) return null;

  const titleId = 'remove-rec-title';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      <div
        className='fixed inset-0 bg-black/50'
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role='button'
        tabIndex={0}
        aria-label='Close modal'
      />
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        className='relative z-10 mx-4 w-full max-w-[420px] overflow-hidden rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-lg)]'
      >
        <div className='px-7 pb-[22px] pt-[26px]'>
          <div className='mb-4 flex h-[44px] w-[44px] items-center justify-center rounded-[12px] bg-[rgba(176,48,48,0.08)] text-[#b03030]'>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6' />
            </svg>
          </div>

          <h3 id={titleId} className='m-0 mb-2 font-display text-2xl font-normal tracking-[-0.015em] text-[var(--n900)]'>
            Remove this recipient?
          </h3>
          <p className='m-0 text-sm leading-[1.55] text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
            <strong className='font-medium text-[var(--n800)]'>{recipientName}</strong> will be removed from your saved recipients. Past transactions stay in your history. You can add them again at any time.
          </p>
        </div>

        <div className='flex justify-end gap-2.5 border-t border-[var(--color-border)] bg-[var(--off)] px-5 py-[14px]'>
          <button
            type='button'
            onClick={onClose}
            className='inline-flex items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-4 py-2.5 text-[13px] font-medium text-[var(--n800)] hover:bg-[var(--off)] font-[family-name:var(--font-ui)]'
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={onConfirm}
            disabled={isDeleting}
            className='inline-flex items-center justify-center gap-2 rounded-lg border-0 bg-[#b03030] px-[18px] py-2.5 text-[13px] font-medium text-white shadow-[var(--shadow-cta)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 font-[family-name:var(--font-ui)]'
          >
            <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z' />
            </svg>
            {isDeleting ? 'Removing…' : 'Remove recipient'}
          </button>
        </div>
      </div>
    </div>
  );
}

DeleteConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  recipientName: PropTypes.string.isRequired,
  isDeleting: PropTypes.bool.isRequired,
};

function DetailRow({ label, value, valueClassName = '' }) {
  return (
    <div className='flex items-center justify-between gap-6 border-b border-[var(--color-border)] px-6 py-[13px] last:border-b-0'>
      <span className='shrink-0 text-[12.5px] font-medium text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>{label}</span>
      <span
        className={`max-w-[70%] break-words text-right text-[13.5px] font-medium text-[var(--n900)] font-[family-name:var(--font-ui)] ${valueClassName}`}
      >
        {value}
      </span>
    </div>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  valueClassName: PropTypes.string,
};

function RecipientDetail() {
  const { recipientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { preferences } = useNumberFormat();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [waitingForDelete, setWaitingForDelete] = useState(false);
  const [txPage, setTxPage] = useState(1);
  const { recipients, gettingRecipients, getRecipientsError, deletingRecipient, deleteRecipientError } = useSelector((state) => state.recipients);
  const { transactions, gettingTransactions } = useSelector((state) => state.transactions);
  const { currencies: configCurrencies } = useSelector((state) => state.config);

  useEffect(() => {
    dispatch(recipientsActions.clearGetRecipientError());
    dispatch(recipientsActions.getRecipients());
    dispatch(transactionsActions.getTransactions());
  }, [dispatch, recipientId]);

  useEffect(() => {
    if (waitingForDelete && deletingRecipient === false) {
      if (!deleteRecipientError) {
        navigate('/recipients', { replace: true });
      }
      setWaitingForDelete(false);
      setDeleteModalOpen(false);
    }
  }, [waitingForDelete, deletingRecipient, deleteRecipientError, navigate]);

  const resolvedRecipient = useMemo(() => {
    const list = Array.isArray(recipients) ? recipients : [];
    return list.find((r) => String(r.id) === String(recipientId)) || null;
  }, [recipients, recipientId]);

  const countryInfo = useMemo(
    () => (resolvedRecipient ? getCountryInfo(resolvedRecipient, configCurrencies) : { code: null, name: '—', flag: null }),
    [resolvedRecipient, configCurrencies],
  );

  const recipientTransactions = useMemo(
    () => (resolvedRecipient ? getTransactionsForRecipient(transactions, resolvedRecipient) : []),
    [transactions, resolvedRecipient],
  );

  useEffect(() => {
    setTxPage(1);
  }, [recipientId, resolvedRecipient?.id, recipientTransactions.length]);

  const txTotalItems = recipientTransactions.length;
  const txTotalPages = Math.max(1, Math.ceil(txTotalItems / TX_ITEMS_PER_PAGE));
  const txStartIndex = (txPage - 1) * TX_ITEMS_PER_PAGE;
  const paginatedTransactions = recipientTransactions.slice(txStartIndex, txStartIndex + TX_ITEMS_PER_PAGE);
  const txEndItem = Math.min(txPage * TX_ITEMS_PER_PAGE, txTotalItems);

  const txVisiblePages = useMemo(() => {
    const pages = [];
    if (txTotalPages <= 5) {
      for (let i = 1; i <= txTotalPages; i += 1) pages.push(i);
    } else if (txPage <= 3) {
      pages.push(1, 2, 3, '…', txTotalPages);
    } else if (txPage >= txTotalPages - 2) {
      pages.push(1, '…', txTotalPages - 2, txTotalPages - 1, txTotalPages);
    } else {
      pages.push(1, '…', txPage, '…', txTotalPages);
    }
    return pages;
  }, [txPage, txTotalPages]);

  const stats = useMemo(() => {
    const pending = recipientTransactions.filter((t) => PENDING_STATUSES.includes(t.status)).length;
    const totalSent = recipientTransactions.reduce((sum, t) => sum + (Number(t.amountIn) || 0), 0);
    const lastTransfer = recipientTransactions[0];
    return {
      totalSent,
      transfers: recipientTransactions.length,
      pending,
      lastTransferDate: lastTransfer ? formatTransferDate(lastTransfer.started || lastTransfer.dateAdded) : '—',
    };
  }, [recipientTransactions]);

  const loading = gettingRecipients || (gettingTransactions && !transactions);
  const loadError = getRecipientsError;
  const isBusiness = resolvedRecipient ? isBusinessRecipient(resolvedRecipient) : false;
  const isActive = resolvedRecipient ? isRecipientActive(resolvedRecipient) : false;
  const canSend = resolvedRecipient ? canSendToRecipient(resolvedRecipient) : false;
  const recipientName = resolvedRecipient?.name || 'Recipient details';

  const handleDeleteConfirm = () => {
    if (!resolvedRecipient) return;
    setWaitingForDelete(true);
    dispatch(
      recipientsActions.deleteRecipient({
        offRampProvider: resolvedRecipient.offRampProvider,
        recipientId: resolvedRecipient.id,
      }),
    );
  };

  const handleSendMoney = () => {
    if (!resolvedRecipient?.id) return;
    navigate(`/send-money/${encodeURIComponent(resolvedRecipient.id)}`);
  };

  const handleBackToRecipients = () => navigate('/recipients');

  return (
    <Layout
      hideHeader
      mainPaddingClassName='px-6 py-9 sm:px-11'
      floatBack={{ onClick: handleBackToRecipients, label: 'Back', ariaLabel: 'Back to recipients' }}
    >
      <div className='flex flex-col gap-7'>
        <DashboardHeader navLabel='Recipients' title={recipientName} />

        <div className='mx-auto flex w-full max-w-[960px] flex-col gap-5'>
          {loading && !resolvedRecipient && (
            <div className={`${CARD_CLASS} animate-pulse px-6 py-12`}>
              <div className='mx-auto h-14 w-14 rounded-2xl bg-[var(--off)]' />
              <div className='mx-auto mt-4 h-6 w-48 rounded bg-[var(--off)]' />
            </div>
          )}

          {loadError && !resolvedRecipient && (
            <ErrorMessage error={loadError} onDismiss={() => dispatch(recipientsActions.getRecipients())} />
          )}

          {!loading && !resolvedRecipient && !loadError && (
            <div className={`${CARD_CLASS} px-6 py-12 text-center`}>
              <p className='mb-4 text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>Recipient not found.</p>
              <button
                type='button'
                onClick={() => navigate('/recipients')}
                className='inline-flex items-center rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-4 py-2 text-sm font-medium text-[var(--n800)] hover:bg-[var(--off)] font-[family-name:var(--font-ui)]'
              >
                Back to recipients
              </button>
            </div>
          )}

          {resolvedRecipient && (
            <>
              <div className={`${CARD_CLASS} px-6 py-7 sm:px-8`}>
                <div className='flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between'>
                  <div className='flex min-w-0 items-start gap-4'>
                    <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[var(--n900)] text-lg font-semibold text-white font-[family-name:var(--font-ui)]'>
                      {getInitials(resolvedRecipient.name)}
                    </div>
                    <div className='min-w-0 flex-1'>
                      <div className='mb-2 flex flex-wrap items-center gap-2.5'>
                        <h2 className='m-0 font-display text-[26px] font-normal leading-[1.1] tracking-[-0.015em] text-[var(--n900)]'>
                          {resolvedRecipient.name || '—'}
                        </h2>
                        {isActive && (
                          <span className='inline-flex items-center gap-1.5 rounded-full bg-[rgba(26,127,90,0.1)] px-2.5 py-0.5 text-[11px] font-semibold tracking-[0.02em] text-[var(--color-success)] font-[family-name:var(--font-ui)]'>
                            <span className='h-[5px] w-[5px] rounded-full bg-current' />
                            Active
                          </span>
                        )}
                      </div>
                      <div className='flex flex-wrap items-center gap-2 text-[13px] text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
                        <span className='inline-flex items-center gap-1.5 font-medium text-[var(--n800)]'>
                          <RecipientFlagBadge currencyCode={resolvedRecipient.currency} />
                          {countryInfo.name}
                        </span>
                        <span className='text-[var(--color-border)]'>·</span>
                        <span className='capitalize'>{isBusiness ? 'business' : 'individual'}</span>
                        <span className='text-[var(--color-border)]'>·</span>
                        <span>
                          Pays in{' '}
                          <strong className='font-semibold tracking-[0.04em] text-[var(--n900)]'>
                            {resolvedRecipient.currency || '—'}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className='flex flex-wrap gap-2.5 sm:justify-end'>
                    <button
                      type='button'
                      onClick={() => setDeleteModalOpen(true)}
                      className='inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-[18px] py-[11px] text-[13px] font-medium text-[#b03030] font-[family-name:var(--font-ui)] hover:bg-[var(--off)]'
                    >
                      <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                        <path d='M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6' />
                      </svg>
                      Remove
                    </button>
                    <button
                      type='button'
                      onClick={handleSendMoney}
                      disabled={!canSend}
                      className='inline-flex cursor-pointer items-center gap-2 rounded-lg border-0 bg-[var(--n900)] px-[18px] py-[11px] text-[13px] font-medium text-white shadow-[var(--shadow-cta)] font-[family-name:var(--font-ui)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                        <path d='M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z' />
                      </svg>
                      Send money
                    </button>
                  </div>
                </div>
              </div>

              <div className={`${CARD_CLASS} grid overflow-hidden sm:grid-cols-2 lg:grid-cols-4`}>
                {[
                  { label: 'Total sent (EUR)', value: formatEuroTotal(stats.totalSent, preferences) },
                  { label: 'Transfers', value: stats.transfers },
                  { label: 'Pending', value: stats.pending },
                  { label: 'Last transfer', value: stats.lastTransferDate },
                ].map((stat, index) => (
                  <div
                    key={stat.label}
                    className={`px-6 py-4 ${index < 3 ? 'border-b border-[var(--color-border)] sm:border-b-0 sm:border-r lg:border-b-0' : ''}`}
                  >
                    <div className='mb-2 text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                      {stat.label}
                    </div>
                    <div className='font-display text-[28px] font-light leading-none tracking-[-0.015em] text-[var(--n900)] tabular-nums'>
                      {stat.value}
                    </div>
                  </div>
                ))}
              </div>

              <div className='grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]'>
                <div className={`${CARD_CLASS} self-start overflow-hidden`}>
                  <div className='border-b border-[var(--color-border)] bg-[var(--off)] px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                    Account information
                  </div>
                  <div className='py-2'>
                    <DetailRow
                      label='Account holder'
                      value={resolvedRecipient.name || '—'}
                    />
                    <DetailRow label='Type' value={<span className='capitalize'>{isBusiness ? 'business' : 'individual'}</span>} />
                    <DetailRow
                      label='Country'
                      value={
                        <span className='inline-flex items-center justify-end gap-2'>
                          <RecipientFlagBadge currencyCode={resolvedRecipient.currency} />
                          <span>{countryInfo.name}</span>
                        </span>
                      }
                    />
                    <DetailRow
                      label='Bank'
                      value={getBankLabel(resolvedRecipient)}
                    />
                    <DetailRow
                      label='IBAN / account'
                      value={formatAccountDisplay(resolvedRecipient)}
                      valueClassName='tracking-[0.02em]'
                    />
                    <DetailRow label='Currency' value={resolvedRecipient.currency || '—'} />
                    <DetailRow
                      label='Email'
                      value={getRecipientEmail(resolvedRecipient)}
                    />
                  </div>
                </div>

                <div className={`${CARD_CLASS} overflow-hidden`}>
                  <div className='flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--off)] px-6 py-4'>
                    <div className='text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                      Transaction history
                    </div>
                    <div className='flex items-center gap-3.5'>
                      <span className='text-[11px] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                        {stats.transfers} transfer{stats.transfers === 1 ? '' : 's'}
                      </span>
                      <Link
                        to='/transaction-history'
                        className='text-[11.5px] font-medium text-[var(--n800)] no-underline hover:underline font-[family-name:var(--font-ui)]'
                      >
                        View in transactions →
                      </Link>
                    </div>
                  </div>

                  {recipientTransactions.length === 0 && (
                    <div className='px-6 py-10 text-center text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
                      No transfers to this recipient yet.
                    </div>
                  )}

                  {paginatedTransactions.map((transaction, index) => {
                    const statusLabel = getTransactionStatusLabel(transaction.status);
                    const statusColor = getTransactionStatusColor(transaction.status);
                    const title = getTransactionTitle(transaction);
                    const dateLabel = formatTransferDate(transaction.started || transaction.dateAdded);

                    return (
                      <div
                        key={transaction.requestId}
                        role='button'
                        tabIndex={0}
                        onClick={() => navigate(`/transaction-history/${transaction.requestId}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/transaction-history/${transaction.requestId}`);
                          }
                        }}
                        className={`${TX_ROW_GRID} cursor-pointer px-6 py-3.5 transition-colors hover:bg-[var(--off)] ${index < paginatedTransactions.length - 1 ? 'border-b border-[var(--color-border)]' : ''
                          }`}
                      >
                        <div className='flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--off)] text-[var(--n700)]'>
                          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                            <path d='M5 12h14M13 6l6 6-6 6' />
                          </svg>
                        </div>
                        <div className='min-w-0'>
                          <div className='truncate text-[13.5px] font-medium text-[var(--n900)] font-[family-name:var(--font-ui)]'>{title}</div>
                          <div className='mt-0.5 text-[11.5px] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>{dateLabel}</div>
                        </div>
                        <span
                          className='shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize font-[family-name:var(--font-ui)]'
                          style={{ color: statusColor, background: `color-mix(in oklab, ${statusColor} 10%, transparent)` }}
                        >
                          {statusLabel}
                        </span>
                        <div className='min-w-[130px] text-right text-sm font-medium tabular-nums text-[var(--n900)] font-[family-name:var(--font-ui)]'>
                          {formatEuroAmount(transaction.amountIn, preferences)}
                        </div>
                        <div className='flex w-[14px] items-center justify-center text-[var(--text-tertiary)]'>
                          <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                            <path d='M9 18l6-6-6-6' />
                          </svg>
                        </div>
                      </div>
                    );
                  })}

                  {txTotalPages > 1 && (
                    <div className='flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] bg-[var(--white)] px-6 py-3'>
                      <span className='text-[11.5px] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                        Showing {txTotalItems === 0 ? 0 : txStartIndex + 1}–{txEndItem} of {txTotalItems}
                      </span>
                      <div className='flex items-center gap-2'>
                        <button
                          type='button'
                          onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                          disabled={txPage === 1}
                          className='inline-flex h-8 items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-3 text-[12px] font-medium text-[var(--n800)] hover:bg-[var(--off)] disabled:cursor-not-allowed disabled:opacity-50 font-[family-name:var(--font-ui)]'
                        >
                          Prev
                        </button>

                        <div className='flex items-center gap-1'>
                          {txVisiblePages.map((page) =>
                            page === '…' ? (
                              <span key={`tx-ellipsis-${Math.random()}`} className='px-1 text-sm text-[var(--text-tertiary)]'>
                                …
                              </span>
                            ) : (
                              <button
                                key={`tx-page-${page}`}
                                type='button'
                                onClick={() => setTxPage(page)}
                                className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-[12px] font-medium font-[family-name:var(--font-ui)] ${txPage === page
                                  ? 'bg-[var(--n900)] text-white'
                                  : 'border border-[var(--color-border)] bg-[var(--white)] text-[var(--n800)] hover:bg-[var(--off)]'
                                  }`}
                              >
                                {page}
                              </button>
                            ),
                          )}
                        </div>

                        <button
                          type='button'
                          onClick={() => setTxPage((p) => Math.min(txTotalPages, p + 1))}
                          disabled={txPage === txTotalPages}
                          className='inline-flex h-8 items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-3 text-[12px] font-medium text-[var(--n800)] hover:bg-[var(--off)] disabled:cursor-not-allowed disabled:opacity-50 font-[family-name:var(--font-ui)]'
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ErrorToast error={getRecipientsError} />
      <ErrorToast error={deleteRecipientError} />
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        recipientName={resolvedRecipient?.name || 'this recipient'}
        isDeleting={deletingRecipient || false}
      />
    </Layout>
  );
}

export default RecipientDetail;
